import * as React from "react";

/**
 * Una media query como estado de React, con suscripción a los cambios de tamaño.
 * La lógica estaba escrita a mano dentro de `useIsPhone`; ahora la comparten
 * `useIsPhone` y `useIsDesktop`.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    onChange();
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * ¿Estamos en teléfono? (<768px, el corte canónico de las hojas inferiores.)
 *
 * Es la misma lógica que GlassSheet llevaba inline; se comparte porque las
 * superficies con framer-motion (VoiceModalShell, StepExplainerDialog) también
 * necesitan elegir variante — en teléfono la hoja sube desde abajo, en
 * escritorio la ventana centrada hace zoom, y una sola variante con y:"100%"
 * arrastraría la ventana centrada por toda la pantalla.
 */
export function useIsPhone(): boolean {
  return useMediaQuery("(max-width: 767px)");
}

/**
 * ¿Estamos en TABLETA? (768–1023 px: de `md` hasta donde empieza `lg`.)
 *
 * Es el hueco entre los otros dos, y hace falta como hook —y no solo como
 * clase `md:… lg:…`— para lo que no se puede escribir en CSS: la pantalla
 * completa del asistente enciende una clase en el `body`, y eso hay que
 * apagarlo si la ventana se sale del rango (girar la tableta, redimensionar).
 * Con la mitad en CSS y la mitad en JS, las dos mitades acaban divergiendo.
 */
export function useIsTablet(): boolean {
  return useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
}
